import React, { useState } from 'react';
import type { NodeInspectorProps } from './types';
import { FileText, Tag, Eye, Shield, Bookmark, AlertTriangle, Download, Bell, BarChart3, Network, Scale, Flag, Lightbulb } from 'lucide-react';
import { safeStr } from '../../utils/safeStr';
import { OFAC_ESCALATE_LABEL, OFAC_INDICATOR_LINE } from '../../utils/sanctionsCopy';
import { legalService, type NoticeSuggestion } from '../../services/api';

// ─── Action Button ───
const ActionButton: React.FC<{
    icon: React.ReactNode;
    label: string;
    onClick?: () => void;
    variant?: 'default' | 'primary' | 'warning' | 'danger';
    disabled?: boolean;
    testId?: string;
    title?: string;
}> = ({ icon, label, onClick, variant = 'default', disabled, testId, title }) => {
    const styles = {
        default: 'border-border hover:bg-muted text-foreground',
        primary: 'border-blue-300 hover:bg-blue-50 text-blue-700 dark:border-blue-800 dark:hover:bg-blue-900/30 dark:text-primary',
        warning: 'border-amber-300 hover:bg-amber-50 text-amber-700 dark:border-amber-700 dark:hover:bg-amber-900/30 dark:text-amber-300',
        danger: 'border-red-300 hover:bg-red-50 text-red-700 dark:border-red-800 dark:hover:bg-red-900/30 dark:text-red-300',
    };
    return (
        <button
            onClick={onClick}
            disabled={disabled}
            data-testid={testId}
            title={title}
            className={`w-full px-3 py-2 text-xs flex items-center gap-2 border rounded-lg transition-colors disabled:opacity-50 ${styles[variant]}`}
        >
            {icon}
            <span className="font-medium">{label}</span>
        </button>
    );
};

export const ActionsTab: React.FC<NodeInspectorProps> = (props) => {
    const {
        selectedNode,
        nodeHealth,
        onStash,
        onMarkSuspicious,
        onUnflag,
        onEscalateToSuspicious,
        onWatchlist,
        onReport,
        onNotice,
        onLabel,
        onOpenAnalyzeCard,
        onCluster,
        clusterLoading,
    } = props;

    const isUnresolvable = nodeHealth?.status === 'unresolvable';
    const blockLegal = nodeHealth?.blockLegalActions ?? false;

    const isExchangeOrVASP = selectedNode.entity_type === 'Exchange' || selectedNode.entity_type === 'VASP' || selectedNode.entity_type === 'High Risk Exchange';
    const isSanctioned = selectedNode.entity_category === 'sanctioned' || (selectedNode as any).sanctioned;
    const isHighRisk = (selectedNode.risk_score || 0) > 0.7;
    const isMixer = (selectedNode.entity_type || '').toLowerCase() === 'mixer';

    // ── §9j Flag state — derive from entity_tags (single source of truth) ──
    // The Flag button must reflect what the canvas + Stash tray show. All
    // three flag origins land in entity_tags: 'auto_flagged' (backend
    // triage chokepoint when risk_score >= 0.7 / sanctioned / mixer),
    // 'manually_flagged' (user clicked Flag), 'flagged_for_review' (right-
    // click Flag from canvas context menu). The button collapses them
    // into one toggle: if ANY are present → "Unflag", else "Flag".
    const flagTags = ['auto_flagged', 'manually_flagged', 'flagged_for_review'];
    const nodeTags: string[] = Array.isArray((selectedNode as any).entity_tags)
        ? ((selectedNode as any).entity_tags as string[])
        : [];
    const isFlagged = flagTags.some(t => nodeTags.includes(t));
    const isAutoFlagged = nodeTags.includes('auto_flagged');
    // §9j.3 — escalation surface. Show a "Confirm Suspicious" CTA when
    // the node is in REVIEW or AUTO state (i.e., flagged but not yet the
    // officer's verdict). When state is already manually_flagged, no
    // promotion is meaningful — only Unflag is shown.
    const isManuallyConfirmed = nodeTags.includes('manually_flagged');
    const canPromote = isFlagged && !isManuallyConfirmed && !!onEscalateToSuspicious;
    // Reason text — read from triage payload if available, else infer from
    // tags. Surfaces in the button title so officers know WHY a node was
    // auto-flagged before they unflag it.
    const flagReasons: string[] = Array.isArray((selectedNode as any).flag_reasons)
        ? ((selectedNode as any).flag_reasons as string[])
        : (isAutoFlagged
            ? [...(isHighRisk ? ['high_risk'] : []), ...(isSanctioned ? ['sanctioned'] : []), ...(isMixer ? ['mixer_entity'] : [])]
            : []);
    const flagReasonText = flagReasons.length > 0
        ? flagReasons.map(r => r.replace(/_/g, ' ')).join(', ')
        : (nodeTags.includes('manually_flagged') ? 'manually marked suspicious' : 'flagged for review');

    // R-INSPECT-04: Determine recommended action based on node context
    const recommendedAction = (() => {
        if (isSanctioned) return 'escalate';
        if (isExchangeOrVASP) return 'notice';
        if (isHighRisk || isMixer) return 'flag';
        return null;
    })();

    // ── Draft notice (routed subpoena, exchange-typing Item 2) ──
    // For an off-ramp depositor the exchange to subpoena is the one it cashed
    // out into (triage summary.offramp_exchanges); for an exchange wallet it's
    // the node's own entity. /legal/suggest-notice returns the jurisdiction
    // routing + court-ready basis; the officer then opens the generate modal.
    const triageSummary = (props as any).triageData?.summary as any;
    const offrampList: string[] = (triageSummary?.is_offramp_point
        && Array.isArray(triageSummary?.offramp_exchanges))
        ? triageSummary.offramp_exchanges.map((x: any) => safeStr(x)).filter(Boolean)
        : [];
    const draftExchange = offrampList[0]
        || (isExchangeOrVASP ? safeStr(selectedNode.entity_name) : '');
    const canDraftNotice = !!draftExchange && !blockLegal;
    const [suggestion, setSuggestion] = useState<NoticeSuggestion | null>(null);
    const [suggestLoading, setSuggestLoading] = useState(false);
    const [suggestError, setSuggestError] = useState<string | null>(null);
    const [activeDraftExchange, setActiveDraftExchange] = useState<string>('');

    const handleDraftNotice = async (exchangeOverride?: string) => {
        const ex = (exchangeOverride || activeDraftExchange || draftExchange || '').trim();
        if (!ex) return;
        setActiveDraftExchange(ex);
        setSuggestLoading(true);
        setSuggestError(null);
        try {
            const result = await legalService.suggestNotice({
                exchange_name: ex,
                chain: (props as any).chain || (selectedNode as any).chain || 'ETH',
                address: selectedNode.id,
                wallet_role: triageSummary?.offramp_wallet_role ?? triageSummary?.wallet_role ?? null,
                attribution_method: triageSummary?.attribution_method ?? null,
                attribution_tier: triageSummary?.attribution_tier ?? null,
                is_sanctioned: !!(triageSummary?.sanctioned ?? isSanctioned),
            });
            setSuggestion(result);
        } catch (err) {
            setSuggestError('Could not load notice recommendation.');
        } finally {
            setSuggestLoading(false);
        }
    };

    const bucketClass: Record<string, string> = {
        india_direct: 'bg-green-500/10 border-green-500/30 text-green-700 dark:text-green-300',
        india_via_partner: 'bg-amber-500/10 border-amber-500/30 text-amber-700 dark:text-amber-300',
        mlat_required: 'bg-orange-500/10 border-orange-500/30 text-orange-700 dark:text-orange-300',
        non_actionable: 'bg-muted border-border text-muted-foreground',
    };

    return (
        <div className="text-sm space-y-4 pb-4">
            {/* ─── Recommended Action Banner (R-INSPECT-04) ─── */}
            {recommendedAction && (
                <div className={`rounded-lg border p-2.5 ${
                    recommendedAction === 'escalate' ? 'bg-red-500/10 border-red-500/30' :
                    recommendedAction === 'notice' ? 'bg-amber-500/10 border-amber-500/30' :
                    'bg-primary/10 border-primary/30'
                }`}>
                    <div className="text-[10px] uppercase tracking-wider font-bold text-muted-foreground mb-1.5">Recommended Action</div>
                    {recommendedAction === 'escalate' && (
                        <ActionButton
                            icon={<AlertTriangle className="w-3.5 h-3.5" />}
                            label={OFAC_ESCALATE_LABEL}
                            title={OFAC_INDICATOR_LINE}
                            onClick={onMarkSuspicious}
                            variant="danger"
                        />
                    )}
                    {recommendedAction === 'escalate' && (
                        <p className="mt-1.5 text-[10px] leading-snug text-muted-foreground" data-testid="ofac-indicator-line">{OFAC_INDICATOR_LINE}</p>
                    )}
                    {recommendedAction === 'notice' && (
                        <ActionButton
                            icon={<FileText className="w-3.5 h-3.5" />}
                            label={`Send Section 91 Notice to ${safeStr(selectedNode.entity_name) || 'Exchange'}`}
                            onClick={() => onNotice?.()}
                            variant="warning"
                        />
                    )}
                    {recommendedAction === 'flag' && (
                        <>
                            <ActionButton
                                icon={<AlertTriangle className="w-3.5 h-3.5" />}
                                label="Flag for Review — High Risk Address"
                                onClick={onMarkSuspicious}
                                variant="danger"
                            />
                            <div className="mt-1">
                                <ActionButton
                                    icon={<Network className="w-3.5 h-3.5" />}
                                    label="Trace Funds from This Address"
                                    onClick={onCluster}
                                    disabled={clusterLoading}
                                    variant="primary"
                                />
                            </div>
                        </>
                    )}
                </div>
            )}

            {/* ─── Investigation ─── */}
            <div className="space-y-2">
                <div className="text-[11px] uppercase tracking-wide text-muted-foreground font-semibold flex items-center gap-1.5">
                    <Shield className="w-3 h-3" />
                    Investigation
                </div>
                <div className="grid grid-cols-3 gap-1.5">
                    <ActionButton
                        icon={<Tag className="w-3.5 h-3.5" />}
                        label="Label"
                        onClick={onLabel}
                    />
                    <ActionButton
                        icon={<Bookmark className="w-3.5 h-3.5" />}
                        label="Stash"
                        onClick={onStash}
                    />
                    {isFlagged ? (
                        <ActionButton
                            icon={<AlertTriangle className="w-3.5 h-3.5" />}
                            label={isAutoFlagged ? 'Unflag (auto)' : 'Unflag'}
                            onClick={onUnflag}
                            variant="warning"
                        />
                    ) : (
                        <ActionButton
                            icon={<AlertTriangle className="w-3.5 h-3.5" />}
                            label="Flag"
                            onClick={onMarkSuspicious}
                            variant="danger"
                        />
                    )}
                </div>
                {/* Flag-state explainer — only shown when the node IS flagged
                    so officers know WHY before they unflag. Auto-flagged
                    nodes always carry a reason; manual flags say so. */}
                {isFlagged && (
                    <div className="text-[10px] text-amber-700 dark:text-amber-400 px-1 -mt-1 italic flex items-start gap-1">
                        <Flag className="w-3 h-3 mt-0.5 shrink-0" aria-hidden />
                        <span>{isAutoFlagged ? `Auto-flagged: ${flagReasonText}` : `Flagged: ${flagReasonText}`}</span>
                    </div>
                )}
                {/* §9j.3 — Escalation CTA. Surfaces only when flag is in
                    REVIEW or AUTO state. One click promotes to the
                    officer-verdict CONFIRMED state (red-solid badge),
                    saving the Unflag → Flag round-trip. */}
                {canPromote && (
                    <ActionButton
                        icon={<AlertTriangle className="w-3.5 h-3.5" />}
                        label={isAutoFlagged ? 'Confirm Suspicious (system flag)' : 'Confirm Suspicious (review)'}
                        onClick={onEscalateToSuspicious}
                        variant="danger"
                    />
                )}
                <ActionButton
                    icon={<Network className="w-3.5 h-3.5" />}
                    label={clusterLoading ? 'Finding Clusters...' : 'Find Related Addresses'}
                    onClick={onCluster}
                    disabled={clusterLoading || isUnresolvable}
                    variant="primary"
                />
            </div>

            {/* ─── Case & Legal / Subpoena workflow (SUBPOENA-WORKFLOW-1) ─── */}
            <div className="space-y-2 pt-3 border-t border-border" data-testid="subpoena-workflow">
                <div className="text-[11px] uppercase tracking-wide text-muted-foreground font-semibold flex items-center gap-1.5">
                    <FileText className="w-3 h-3" />
                    Subpoena workflow
                </div>
                <p className="text-[10px] text-muted-foreground leading-snug px-0.5">
                    Route a Section 91 / CEX notice from attribution or off-ramp hits.
                    Suggest → review jurisdiction → generate template.
                </p>
                <div className="space-y-1.5">
                    {/* Multi-exchange off-ramp: draft one notice path per exchange */}
                    {canDraftNotice && offrampList.length > 1 && (
                        <div className="space-y-1" data-testid="subpoena-multi-exchange">
                            <div className="text-[10px] text-muted-foreground px-0.5">
                                {offrampList.length} off-ramp exchanges — draft one notice each
                            </div>
                            {offrampList.map(ex => (
                                <ActionButton
                                    key={ex}
                                    icon={<Scale className="w-3.5 h-3.5" />}
                                    label={suggestLoading && activeDraftExchange === ex
                                        ? 'Checking routing…'
                                        : `Draft notice — ${ex}`}
                                    onClick={() => handleDraftNotice(ex)}
                                    disabled={blockLegal || suggestLoading}
                                    testId={`wb-draft-notice-${ex}`}
                                    variant="warning"
                                />
                            ))}
                        </div>
                    )}
                    {/* Single draft notice (routed) */}
                    {canDraftNotice && offrampList.length <= 1 && (
                        <ActionButton
                            icon={<Scale className="w-3.5 h-3.5" />}
                            label={suggestLoading ? 'Checking routing…' : `Draft notice (routed) — ${draftExchange}`}
                            onClick={() => handleDraftNotice()}
                            disabled={blockLegal || suggestLoading}
                            testId="wb-draft-notice-btn"
                            variant="warning"
                        />
                    )}
                    {suggestError && (
                        <div className="text-[10px] text-red-600 dark:text-red-400 px-1 italic">{suggestError}</div>
                    )}
                    {suggestion && (
                        <div className={`rounded-lg border p-2.5 text-xs ${bucketClass[suggestion.jurisdiction_bucket] || bucketClass.non_actionable}`}>
                            <div className="flex items-center justify-between gap-2 mb-1">
                                <span className="font-bold uppercase tracking-wide text-[10px]">{suggestion.jurisdiction_label}</span>
                                {suggestion.recommended_template_key && (
                                    <span className="px-1.5 py-0.5 rounded bg-black/5 dark:bg-white/10 text-[10px] font-mono">
                                        {suggestion.recommended_template_key}
                                    </span>
                                )}
                            </div>
                            <div className="text-foreground/90 leading-snug">{suggestion.basis}</div>
                            {suggestion.notes && (
                                <div className="text-[10px] text-muted-foreground mt-1 leading-snug">{suggestion.notes}</div>
                            )}
                            {suggestion.actionable ? (
                                <div className="mt-2">
                                    <ActionButton
                                        icon={<FileText className="w-3.5 h-3.5" />}
                                        label={`Generate ${suggestion.recommended_template_key || 'notice'} for ${suggestion.exchange_name}`}
                                        onClick={() => onNotice?.(
                                            suggestion.exchange_name
                                            || activeDraftExchange
                                            || draftExchange
                                            || undefined,
                                        )}
                                        disabled={blockLegal}
                                        variant="warning"
                                    />
                                </div>
                            ) : (
                                <div className="text-[10px] font-semibold mt-1.5">Not actionable — no central operator to compel.</div>
                            )}
                        </div>
                    )}
                    <ActionButton
                        icon={<FileText className="w-3.5 h-3.5" />}
                        label={isExchangeOrVASP ? `Section 91 Notice to ${safeStr(selectedNode.entity_name) || 'Exchange'}` : 'Generate Legal Notice'}
                        onClick={() => onNotice?.(activeDraftExchange || draftExchange || undefined)}
                        disabled={blockLegal}
                        variant="warning"
                    />
                    <ActionButton
                        icon={<Download className="w-3.5 h-3.5" />}
                        label="Download all transactions as CSV"
                        onClick={onReport}
                        disabled={blockLegal}
                        testId="wb-report-btn"
                        variant="primary"
                    />
                </div>
            </div>

            {/* ─── Safety & Monitoring ─── */}
            <div className="space-y-2 pt-3 border-t border-border">
                <div className="text-[11px] uppercase tracking-wide text-muted-foreground font-semibold flex items-center gap-1.5">
                    <Bell className="w-3 h-3" />
                    Safety & Monitoring
                </div>
                <div className="grid grid-cols-1 gap-1.5">
                    <ActionButton
                        icon={<Eye className="w-3.5 h-3.5" />}
                        label="Watch & Alert"
                        onClick={onWatchlist}
                        testId="wb-watch-btn"
                        variant="primary"
                    />
                </div>
            </div>

            {/* ─── Graph Analysis ─── */}
            {onOpenAnalyzeCard && (
                <div className="space-y-2 pt-3 border-t border-border">
                    <div className="text-[11px] uppercase tracking-wide text-muted-foreground font-semibold flex items-center gap-1.5">
                        <BarChart3 className="w-3 h-3" />
                        Graph Analysis
                    </div>
                    <ActionButton
                        icon={<BarChart3 className="w-3.5 h-3.5" />}
                        label="AI Summary & Graph Analytics"
                        onClick={onOpenAnalyzeCard}
                        disabled={isUnresolvable}
                        variant="primary"
                    />
                    <div className="text-[10px] text-muted-foreground px-1">
                        Opens Analyze Graph card (keyboard shortcut: <kbd className="px-1 py-0.5 bg-muted rounded border border-border text-[10px] font-mono">G</kbd>)
                    </div>
                </div>
            )}

            {/* ─── Tip ─── */}
            <div className="text-[11px] text-muted-foreground bg-secondary/30 border border-border rounded-lg p-2.5 flex items-start gap-2">
                <Lightbulb className="w-4 h-4 shrink-0 mt-0.5" aria-hidden />
                <span>Enrich intelligence first via the Intelligence tab, then use these tools for legal notices, watchlisting, and report export.</span>
            </div>
        </div>
    );
};
